import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSelector } from 'react-redux';
import { Images } from "../../../utils/Images";
import { isUserLoggedIn } from "../../../utils/authUtils";
import AuthDialog from "../AuthDialog";
import { submitUserCampaignComment, getUserCampaignComments } from "../../../api/services/campaignService";

// Helper function to build image URL
const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const imageBase = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || '';
  if (imageBase && !imagePath.startsWith('/')) {
    return `${imageBase}/${imagePath}`;
  } else if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

// Helper function to calculate time ago
const calculateTimeAgo = (dateString) => {
  if (!dateString) return '';
  
  const now = new Date();
  const commentDate = new Date(dateString);
  const diffInSeconds = Math.floor((now - commentDate) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} sec ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  } else if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
};

const Comments = ({ campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [newComment, setNewComment] = useState('');
  const [userComments, setUserComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  
  // Get user from Redux store
  const { user } = useSelector((state) => state.auth);
  const campaignId = campaignData?.id;

  // Fetch user comments on mount and when campaignId changes
  useEffect(() => {
    const fetchUserComments = async () => {
      try {
        setLoading(true);
        const allComments = await getUserCampaignComments();
        // Filter comments by campaign_id
        const filteredComments = Array.isArray(allComments) 
          ? allComments.filter(comment => comment.campaign_id === campaignId && comment.status === 1)
          : [];
        setUserComments(filteredComments);
      } catch (error) {
        console.error("Error fetching user comments:", error);
        setUserComments([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (campaignId) {
      fetchUserComments();
    }
  }, [campaignId]);

  // Get comments from campaign API (secondary) - only show approved comments (status === 1)
  const apiComments = Array.isArray(campaignData?.comments) 
    ? campaignData.comments.filter(comment => comment.status === 1)
    : [];
  
  // Transform user comments to component format
  const transformedUserComments = userComments.map(comment => ({
    id: `user-${comment.id}`,
    author: comment.username || 'Anonymous',
    time: calculateTimeAgo(comment.createdAt || comment.updatedAt),
    content: comment.message || '',
    avatar: comment.profilepic ? buildImageUrl(comment.profilepic) : null
  }));

  // Transform campaign API comments to component format
  const transformedApiComments = apiComments.map(comment => ({
    id: `api-${comment.id}`,
    author: comment.username || 'Anonymous',
    time: calculateTimeAgo(comment.updatedAt || comment.createdAt),
    content: comment.message || '',
    avatar: comment.profilepic ? buildImageUrl(comment.profilepic) : null
  }));

  // Combine comments: user comments first, then API comments
  const allComments = [...transformedUserComments, ...transformedApiComments];

  const handleAddComment = async () => {
    if (newComment.trim() === '') return;
    
    // Check if user is logged in
    if (!isUserLoggedIn() || !user) {
      setAuthDialogOpen(true);
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare comment data
      const commentData = {
        campaign_id: campaignId,
        user_id: user.id,
        username: user.name || 'Anonymous',
        comments: newComment.trim(),
        status: 0,
      };

      // If user has profile picture, add it
      if (user.profilePicture) {
        // Fetch the profile picture as a file
        try {
          const response = await fetch(buildImageUrl(user.profilePicture));
          const blob = await response.blob();
          const file = new File([blob], 'profile.jpg', { type: blob.type });
          commentData.profile_picture = file;
        } catch (error) {
          console.error("Error fetching profile picture:", error);
        }
      }

      // Submit comment
      const response = await submitUserCampaignComment(commentData);
      
      if (response && response.data) {
        // Clear the input
        setNewComment('');
        
        // Refetch comments to get the latest data
        const allComments = await getUserCampaignComments();
        const filteredComments = Array.isArray(allComments) 
          ? allComments.filter(comment => comment.campaign_id === campaignId && comment.status === 1)
          : [];
        setUserComments(filteredComments);
        // Show thank you message
        setThankYouOpen(true);
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
      alert("Failed to submit comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Use keydown, not keypress: mobile soft keyboards often omit keypress for Enter,
  // so submit-by-Enter only worked on desktop before.
  const handleCommentKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.nativeEvent?.isComposing) return; // IME / mobile composition
    e.preventDefault();
    handleAddComment();
  };

  return (
    <Box sx={{ mx: "auto", p: 2, border: "1px solid #F1F1F1", borderRadius: "12px", padding: isMobile ? "20px 16px" : "30px" }}>
      {/* Header */}
      <Typography variant="h4" component="h2" sx={{display:"flex",gap:"10px", alignItems: "center", fontSize:isMobile?"14px": "24px", fontWeight: '700', mb: isMobile?"16px":"22px", color: '#1D1D1B',borderBottom:isMobile?"1px solid #E6E8EC":"none",paddingBottom:isMobile?"16px":"0px" }}>
       <img src={Images.streamlineSharpStar} alt="Images.streamlineSharpStar" style={{ width: isMobile ? "14px" : "24px", height: isMobile ? "14px" : "24px" }} />  Comments
      </Typography>
      
      {/* Add Comment Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start',border:"1px solid #DDE2E5",padding:isMobile?"5px":"10px",borderRadius:"8px" }}>
          <Box sx={{ flexGrow: 1,padding:"0" ,position:"relative" }}>
            <TextField
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleCommentKeyDown}
              placeholder="Add a comment"
              multiline
              rows={1}
              fullWidth
              variant="outlined"
              sx={{           
                border:"unset ",  
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  padding: '10px 12px ',
                   border:"unset",  
                   '& fieldset': {
                    border: 'none' // This removes the border
                  },
                  '&:hover fieldset': {
                    border: 'none' // Removes border on hover
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none' // Removes border when focused
                  }
                }
              }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                position: isMobile ? 'absolute' : 'relative',
                bottom: isMobile ? '5px' : '0',
                right: '5px',
                // Without z-index, the multiline input sits above the button in hit-testing on mobile taps
                zIndex: isMobile ? 2 : 'auto',
              }}
            >
              <Button
                variant="contained"
                onClick={handleAddComment}
                disabled={submitting}
                sx={{
                  borderRadius: '6px',
                  px:isMobile?"16px": "27px",
                  py: "11px",
                  backgroundColor: '#1D1D1B',
                  fontWeight: '600',
                  fontSize: '12px',
                  lineHeight: '14px',
                  '&:disabled': {
                    backgroundColor: '#ccc',
                  },
                }}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Comments List */}
      <Box>
        {allComments.length === 0 ? (
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              color: "#686866",
              textAlign: "center",
              py: 4,
            }}
          >
            No comments yet. Be the first to comment!
          </Typography>
        ) : (
          allComments.map((comment, index) => (
            <Box key={comment.id} sx={{borderBottom:isMobile?"1px solid rgba(151, 151, 150, 0.50)":"none" ,marginBottom:isMobile?"12px":"0px",paddingBottom:isMobile?"12px":"0px",
                '&:last-of-type': {
                borderBottom: 'none',
                marginBottom: '0px',
                paddingBottom: '0px'
      }}}>
              <Box sx={{ display: 'flex', gap: 2, mb:isMobile?"0px": "24px",boxShadow:isMobile?"unset":"0 4px 13px 0 rgba(70, 79, 175, 0.10)" ,padding:isMobile?"0px 0px 0px  0px":"16px 14px 14px 16px ",borderRadius:isMobile?"0":"14px"
              }}>
               
                <Box sx={{ flexGrow: 1,display:"flex",flexDirection:"column",gap:"14px" }}>
                  <Box sx={{ display: 'flex', alignItems: 'start',gap:"10px", }}>
                   <Avatar 
                  src={comment.avatar || undefined} 
                  sx={{ 
                    bgcolor: comment.avatar ? '#e74c3c' : '#9E9E9E', 
                    width: 36, 
                    height: 36,              
                  }}
                >
                </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column',}}>
                    <Typography variant="subtitle1" sx={{ fontWeight: '700',color:"#000",fontSize:isMobile?"12px":"14px" }}>
                      {comment.author}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '4A4A49',fontSize:isMobile?"10px":"12px",fontWeight:"500" }}>
                      {comment.time}
                    </Typography>
                  </Box>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6,fontSize:isMobile?"10px":"14px",fontWeight:"400", letterSpacing:"-2%" }}>
                    {comment.content}
                  </Typography>
                </Box>
              </Box>
              
            </Box>
          ))
        )}
      </Box>

      {/* Thank you popup dialog */}
      <Dialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '16px', sm: '18px' },
              fontWeight: 700,
              mb: 1.5,
              color: '#1D1D1B',
            }}
          >
            Thank you for your comment!
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '13px', sm: '14px' },
              color: '#686866',
              mb: 2.5,
            }}
          >
            Your comment is under review by the admin.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setThankYouOpen(false)}
            sx={{
              mt: 1,
              px: 4,
              textTransform: 'none',
              backgroundColor: '#1D1D1B',
              '&:hover': {
                backgroundColor: '#2a2a2a',
              },
            }}
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Auth Dialog */}
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        initialView="login"
      />
    </Box>
  );
};

export default Comments;